import { useCallback, useEffect, useRef, useState } from 'react';
import {
  IDLE_MS_MAX,
  IDLE_MS_MIN,
  easeInOutQuad,
  lerp,
  pickWanderTarget,
  segment,
  type Point,
} from './wanderMath';

export type WanderMotion = 'idle' | 'walk' | 'run';
export type WanderFacing = 'left' | 'right';

const START_POS: Point = { x: 44, y: 78 };
const FACE_DX = 0.5;

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useHabitatWander(): {
  pos: Point;
  motion: WanderMotion;
  facing: WanderFacing;
  pause: () => void;
  resume: () => void;
  goTo: (target: Point, opts?: { forceRun?: boolean }) => Promise<void>;
  stop: () => void;
} {
  const [pos, setPos] = useState<Point>(START_POS);
  const [motion, setMotion] = useState<WanderMotion>('idle');
  const [facing, setFacing] = useState<WanderFacing>('right');

  const posRef = useRef<Point>(START_POS);
  const pausedRef = useRef(true);
  const directedRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const goToResolveRef = useRef<(() => void) | null>(null);
  const scheduleIdleRef = useRef<() => void>(() => {});

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current != null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const resolvePendingGoTo = useCallback(() => {
    const resolve = goToResolveRef.current;
    goToResolveRef.current = null;
    resolve?.();
  }, []);

  const finishGoTo = useCallback(() => {
    directedRef.current = false;
    setMotion('idle');
    resolvePendingGoTo();
  }, [resolvePendingGoTo]);

  const animateTo = useCallback((target: Point, opts?: { forceRun?: boolean; directed?: boolean }) => {
    const from = posRef.current;
    const dx = target.x - from.x;
    if (Math.abs(dx) >= FACE_DX) {
      setFacing(dx > 0 ? 'right' : 'left');
    }
    const plan = segment(from, target, {
      forceRun: opts?.forceRun,
      directed: opts?.directed,
      reducedMotion: prefersReducedMotion(),
    });
    setMotion(plan.motion);
    const started = performance.now();

    const tick = (now: number) => {
      const t = plan.durationMs <= 0 ? 1 : Math.min(1, (now - started) / plan.durationMs);
      const next = lerp(from, target, easeInOutQuad(t));
      posRef.current = next;
      setPos(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      rafRef.current = null;
      finishGoTo();
      if (!pausedRef.current && !directedRef.current) scheduleIdleRef.current();
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [finishGoTo]);

  const scheduleIdle = useCallback(() => {
    if (pausedRef.current || directedRef.current) return;
    if (idleTimerRef.current != null) return;
    const wait = IDLE_MS_MIN + Math.random() * (IDLE_MS_MAX - IDLE_MS_MIN);
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      if (pausedRef.current || directedRef.current) return;
      animateTo(pickWanderTarget(posRef.current, Math.random));
    }, wait);
  }, [animateTo]);
  scheduleIdleRef.current = scheduleIdle;

  // Pause stops AUTONOMOUS wandering: the idle timer and an idle walk. A directed `goTo` is an
  // explicit order (a fetch, a food drop, a hide) and runs to its end — `tick` sees `pausedRef`
  // and schedules no idle walk after it. QA 2026-09-23 bug 7: `HabitatScreen` pauses in the same
  // commit a playlet mounts and sends the pet to its first hiding spot (child effects run first),
  // so cancelling the directed walk here meant the pet never hid on round 1. `stop()` below is
  // the hard variant that cancels everything.
  const pause = useCallback(() => {
    pausedRef.current = true;
    if (idleTimerRef.current != null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (directedRef.current) return;
    clearTimers();
    setMotion('idle');
  }, [clearTimers]);

  const resume = useCallback(() => {
    pausedRef.current = false;
    scheduleIdle();
  }, [scheduleIdle]);

  const stop = useCallback(() => {
    pausedRef.current = true;
    directedRef.current = false;
    clearTimers();
    setMotion('idle');
    resolvePendingGoTo();
  }, [clearTimers, resolvePendingGoTo]);

  const goTo = useCallback((target: Point, opts?: { forceRun?: boolean }) => {
    directedRef.current = true;
    clearTimers();
    resolvePendingGoTo();
    return new Promise<void>((resolve) => {
      goToResolveRef.current = resolve;
      animateTo(target, { ...opts, directed: true });
    });
  }, [animateTo, clearTimers, resolvePendingGoTo]);

  useEffect(() => () => {
    clearTimers();
    resolvePendingGoTo();
  }, [clearTimers, resolvePendingGoTo]);

  return { pos, motion, facing, pause, resume, goTo, stop };
}
